import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ArrowRightOnRectangleIcon,
  ArrowUpTrayIcon,
  ShoppingCartIcon,
} from '@heroicons/react/24/outline';
import ProfileDropdown from './profileDropDown';

const mobileIconBtn =
  'inline-flex shrink-0 items-center justify-center rounded border border-gray-200 p-2 transition hover:bg-gray-50';

export default function NavBar({ isAuthenticated, currentView, onNavigate, onLogout }) {
  const location = useLocation();
  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const onShopPage = location.pathname === '/shop';

  const navButton = (label, view, style = '') =>
    currentView !== view && (
      <button type="button" onClick={() => onNavigate(view)} className={`cursor-pointer ${style}`}>
        {label}
      </button>
    );

  const shopLinkDesktop = !onShopPage ? (
    <Link
      to="/shop"
      aria-label="Shop"
      title="Shop"
      className="inline-flex items-center text-gray-700 hover:text-violet-900"
    >
      <ShoppingCartIcon className="h-6 w-6" aria-hidden />
    </Link>
  ) : null;

  const shopLinkMobile = !onShopPage ? (
    <Link
      to="/shop"
      aria-label="Shop"
      title="Shop"
      className={`${mobileIconBtn} text-gray-700`}
    >
      <ShoppingCartIcon className="h-5 w-5" aria-hidden />
    </Link>
  ) : null;

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-white shadow-md border-b">
      <div className="px-4 md:px-6 py-3 flex items-center justify-between gap-2">
        {/* Brand */}
        <div
          className="flex min-w-0 items-center gap-2 cursor-pointer"
          onClick={() => (isAuthenticated ? onNavigate('dashboard') : onNavigate('home'))}
        >
          <img
            src="/poppyruz_icon.png"
            alt="Poppyruz"
            className="h-7 w-7 shrink-0"
            loading="eager"
            decoding="async"
            draggable="false"
          />
          <span className="truncate text-xl font-bold">Poppyruz</span>
        </div>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center space-x-6">
          {isAuthenticated ? (
            <>
              {shopLinkDesktop}
              <div
                className="relative"
                onMouseEnter={() => setShowUploadMenu(true)}
                onMouseLeave={() => setShowUploadMenu(false)}
              >
                <button
                  type="button"
                  className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700"
                >
                  Upload
                </button>

                {showUploadMenu && (
                  <div className="absolute left-0 top-full w-44 bg-white border rounded shadow z-50">
                    <div
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        setShowUploadMenu(false);
                        onNavigate('upload');
                      }}
                    >
                      Upload Book
                    </div>
                    <div
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      onClick={() => {
                        setShowUploadMenu(false);
                        onNavigate('upload_theme');
                      }}
                    >
                      Upload Theme
                    </div>
                  </div>
                )}
              </div>

              {navButton('Dashboard', 'dashboard', 'hover:text-violet-900')}
              {navButton('My Books', 'my_books', 'hover:text-violet-900')}
              {navButton('Public Books', 'books', 'hover:text-violet-900')}
              <Link to="/blogs" className="hover:text-violet-900 cursor-pointer">
                Blogs
              </Link>
              <Link to="/author" className="hover:text-violet-900 cursor-pointer">
                Author
              </Link>

              <div className="w-10 h-10 flex items-center cursor-pointer">
                <ProfileDropdown onLogout={onLogout} />
              </div>
            </>
          ) : (
            <>
              {shopLinkDesktop}
              {navButton('Home', 'home', 'hover:text-violet-900')}
              {navButton('Books', 'books', 'hover:text-violet-900')}
              <Link to="/blogs" className="hover:text-violet-900 cursor-pointer">
                Blogs
              </Link>
              {currentView === 'login'
                ? navButton('Sign Up', 'signup', 'bg-violet-900 text-white px-4 py-2 rounded hover:bg-violet-950')
                : navButton('Upload', 'upload', 'bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700')}
              {navButton('Login', 'login', 'bg-violet-900 text-white px-4 py-2 rounded hover:bg-violet-950')}
            </>
          )}
        </nav>

        {/* Mobile controls */}
        <div className="flex shrink-0 items-center gap-1.5 md:hidden">
          {shopLinkMobile}

          {!isAuthenticated ? (
            <>
              <button
                type="button"
                aria-label="Upload"
                title="Upload"
                onClick={() => onNavigate('upload')}
                className={`${mobileIconBtn} text-orange-600`}
              >
                <ArrowUpTrayIcon className="h-5 w-5" aria-hidden />
              </button>
              <button
                type="button"
                aria-label="Log in"
                title="Log in"
                onClick={() => onNavigate('login')}
                className={`${mobileIconBtn} text-violet-900`}
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" aria-hidden />
              </button>
            </>
          ) : null}

          <button
            type="button"
            aria-label="Menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen((v) => !v)}
            className={mobileIconBtn}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {mobileOpen && (
        <div className="md:hidden border-t bg-white">
          <div className="px-4 py-3 grid gap-2 text-sm">
            {!isAuthenticated ? (
              <button
                type="button"
                onClick={() => {
                  onNavigate('home');
                  setMobileOpen(false);
                }}
                className="text-left"
              >
                Home
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => {
                onNavigate('books');
                setMobileOpen(false);
              }}
              className="text-left"
            >
              Books
            </button>
            <Link
              to="/blogs"
              onClick={() => setMobileOpen(false)}
              className="text-left hover:text-violet-900"
            >
              Blogs
            </Link>
            {!onShopPage ? (
              <Link
                to="/shop"
                onClick={() => setMobileOpen(false)}
                className="text-left hover:text-violet-900"
              >
                Shop
              </Link>
            ) : null}

            {isAuthenticated ? (
              <>
                <button
                  type="button"
                  onClick={() => {
                    onNavigate('dashboard');
                    setMobileOpen(false);
                  }}
                  className="text-left"
                >
                  Dashboard
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onNavigate('my_books');
                    setMobileOpen(false);
                  }}
                  className="text-left"
                >
                  My Books
                </button>
                <Link
                  to="/author"
                  onClick={() => setMobileOpen(false)}
                  className="text-left hover:text-violet-900"
                >
                  Author
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    onNavigate('upload');
                    setMobileOpen(false);
                  }}
                  className="text-left"
                >
                  Upload Book
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onNavigate('upload_theme');
                    setMobileOpen(false);
                  }}
                  className="text-left"
                >
                  Upload Theme
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onLogout();
                    setMobileOpen(false);
                  }}
                  className="text-left text-red-600"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    onNavigate('upload');
                    setMobileOpen(false);
                  }}
                  className="text-left"
                >
                  Upload Book
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onNavigate('login');
                    setMobileOpen(false);
                  }}
                  className="text-left"
                >
                  Login
                </button>
                <button
                  type="button"
                  onClick={() => {
                    onNavigate('signup');
                    setMobileOpen(false);
                  }}
                  className="text-left"
                >
                  Sign Up
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
